import { createHostBridge } from "./host-bridge.js";
import { GraphView } from "./render.js";
import { showContextMenu, closeContextMenu, type MenuItem } from "./contextMenu.js";
import { toggleSettings } from "./settings.js";
import { openNewBranchDialog, closeNewBranchDialog } from "./newBranchDialog.js";
import {
  openChangesDialog,
  closeChangesDialog,
  setChangesFiles,
  setCommitTree,
  setTreeContentSearchResults,
  setFileDiff,
  setFileContent,
} from "./changesDialog.js";
import {
  openCommitDialog,
  closeCommitDialog,
  setWorkingTreeFiles,
  setWorkingTreeFileDiff,
} from "./commitDialog.js";
import { openMergeDialog, closeMergeDialog, setMergePreview, setMergeFileDiff } from "./mergeDialog.js";
import { getBranchDialogMode } from "./branchDialogMode.js";
import { getMainBranch, onMainBranchChange } from "./mainBranch.js";
import { getDisplayMode, onDisplayModeChange } from "./displayMode.js";
import { showsMergedInTarget, onMergedViewChange } from "./mergedView.js";
import { getThemeChoice, onThemeChange, LIGHT_THEME, DARK_THEME } from "./theme.js";
import { t, onLangChange, type MsgKey } from "./i18n.js";
import { getGitMode, getCustomGitPath, onGitSourceChange, receiveGitPathFromBrowse } from "./gitPathSetting.js";
import type { GraphData, ThemeTokens, OpKind, OpResult } from "@rev-graph/protocol";
import type { PositionedCommit } from "@rev-graph/graph-core";
import "./style.css";

// Injected by esbuild's `define` (see build.mjs) from the repo root package.json.
declare const __APP_VERSION__: string;

const GITHUB_URL = "https://github.com/HunKonTech/git_Revision_Graph";

const DEFAULT_THEME: ThemeTokens = {
  kind: "dark",
  background: "#1e1e1e",
  foreground: "#d4d4d4",
  accent: "#0e639c",
  border: "#3c3c3c",
};

function boot(): void {
  const root = document.getElementById("app") ?? document.body;

  const statusBar = document.createElement("div");
  statusBar.className = "status-bar";

  const statusText = document.createElement("span");
  statusText.className = "status-bar-text";
  statusBar.appendChild(statusText);

  const footerLinks = document.createElement("div");
  footerLinks.className = "status-bar-footer";
  const versionLabel = document.createElement("span");
  versionLabel.className = "footer-version";
  versionLabel.textContent = `v${__APP_VERSION__}`;
  const githubLink = document.createElement("a");
  githubLink.className = "footer-github";
  githubLink.href = GITHUB_URL;
  githubLink.textContent = t("footer.github");
  githubLink.title = GITHUB_URL;
  footerLinks.appendChild(versionLabel);
  footerLinks.appendChild(githubLink);
  statusBar.appendChild(footerLinks);

  // The current status is held as a closure so it can be re-rendered in the
  // active language whenever the user switches languages.
  let statusFn: () => string = () => t("status.loading");
  function renderStatus(fn: () => string): void {
    statusFn = fn;
    statusText.textContent = statusFn();
  }
  renderStatus(statusFn);

  const canvas = document.createElement("div");
  canvas.className = "graph-canvas";
  canvas.style.position = "relative";
  // Hovering a legend row previews that kind in the graph. `view` is created a
  // few lines below; the closure only runs on hover, long after that.
  const legend = buildLegend((kind) => view.setLegendHighlight(kind));
  canvas.appendChild(legend);

  const detailsPanel = buildDetailsPanel();
  // Node whose details the panel currently shows, or null when nothing is
  // selected — the panel is hidden exactly then.
  let detailsNodeId: string | null = null;
  const hideDetails = (): void => {
    detailsNodeId = null;
    detailsPanel.dataset.hidden = "";
  };
  const openDetails = (commit: PositionedCommit): void => {
    detailsNodeId = commit.nodeId;
    showCommitDetails(detailsPanel, commit, currentHead, hideDetails);
  };
  // Sha of the currently checked-out commit, kept so the details panel can flag
  // the commit the working tree is on.
  let currentHead: string | null = null;
  // Last graph payload, retained so the view can be re-laid-out when the main
  // branch selection changes without a host round-trip.
  let lastData: GraphData | null = null;

  const mainContent = document.createElement("div");
  mainContent.className = "main-content";
  mainContent.appendChild(canvas);
  mainContent.appendChild(detailsPanel);

  root.appendChild(mainContent);
  root.appendChild(statusBar);

  const bridge = createHostBridge();

  // Route the click through the host so every embedding (VS Code, VS, the JVM
  // IDE hosts) opens it in the system browser instead of navigating in-place.
  githubLink.addEventListener("click", (e) => {
    e.preventDefault();
    bridge.post({ type: "openExternal", url: GITHUB_URL });
  });

  const view = new GraphView(canvas, {
    onNodeContextMenu(commit, x, y) {
      // Stash nodes get their own apply/pop/drop menu.
      if (commit.stash && commit.stashIndex != null) {
        const index = commit.stashIndex;
        showContextMenu(x, y, [
          {
            label: t("menu.stashApply"),
            action: () => {
              renderStatus(() => t("status.stashApplying"));
              bridge.post({ type: "stashApply", index });
            },
          },
          {
            label: t("menu.stashPop"),
            action: () => {
              renderStatus(() => t("status.stashPopping"));
              bridge.post({ type: "stashPop", index });
            },
          },
          {
            label: t("menu.stashDrop"),
            separatorBefore: true,
            action: () => {
              renderStatus(() => t("status.stashDropping"));
              bridge.post({ type: "stashDrop", index });
            },
          },
        ]);
        return;
      }

      const sha = commit.sha;
      // A merged-in copy carries no refs of its own (they belong on the box in
      // the branch the commit was written on). Read the ref-driven entries from
      // that original box so the copy's menu offers exactly the same actions.
      const refSource = commit.mergedIn ? view.getPositionedCommit(sha) ?? commit : commit;
      const localBranches = refSource.refs.filter((r) => r.type === "localBranch");
      const allRefs = lastData?.refs ?? [];
      // The branch currently checked out — the merge target.
      const currentBranch = allRefs.find((r) => r.type === "localBranch" && r.isCurrent)?.name;

      const items: MenuItem[] = [
        {
          label: t("menu.createBranch"),
          action: () => startCreateBranch(sha, refSource.refs),
        },
        {
          label: t("menu.checkout"),
          action: () => bridge.post({ type: "checkout", sha, ref: boxBranchRef(refSource) }),
        },
      ];

      // View the files this commit changed (skip phantom branch placeholders,
      // which carry no commit of their own).
      if (!commit.phantom) {
        items.push({
          label: t("menu.viewChanges"),
          action: () => startViewChanges(sha),
        });
      }

      // Rename is only offered for local (unpushed) commits, and never on phantom
      // branch placeholders (which carry no real commit of their own).
      if (commit.unpushed && !commit.phantom) {
        items.push({
          label: t("menu.renameCommit"),
          action: () => bridge.post({ type: "renameCommit", sha }),
        });
      }

      // Undo is only offered for local (unpushed) commits, and never on phantom
      // branch placeholders (which carry no real commit of their own).
      if (commit.unpushed && !commit.phantom) {
        items.push({
          label: t("menu.undoCommit"),
          action: () => {
            renderStatus(() => t("status.undoing"));
            bridge.post({ type: "undoCommit", sha });
          },
        });
      }

      // On a merged-in copy, offer a jump to the box on the branch the commit
      // was actually written on — the copy is the same commit, so every other
      // entry above already acts on the right sha.
      if (commit.mergedIn) {
        items.push({
          label: t("menu.jumpToOriginal"),
          action: () => view.jumpToNode(sha),
        });
      }

      items.push({
        label: t("menu.copySha"),
        separatorBefore: true,
        action: () => bridge.post({ type: "copySha", sha }),
      });

      // One push/delete entry per local branch that points at this commit.
      // "Push" is offered only when the branch has no remote counterpart yet.
      localBranches.forEach((ref, i) => {
        // Merge this branch into the current checkout (not offered for the branch
        // already checked out — you can't merge a branch into itself).
        if (currentBranch && ref.name !== currentBranch) {
          items.push({
            label: t("menu.mergeBranch", { source: ref.name, target: currentBranch }),
            separatorBefore: i === 0,
            action: () => startMerge(ref.name, currentBranch),
          });
        }
        const hasRemote = allRefs.some(
          (r) => r.type === "remoteBranch" && r.name.endsWith("/" + ref.name),
        );
        if (!hasRemote) {
          items.push({
            label: t("menu.pushBranch", { name: ref.name }),
            separatorBefore: i === 0,
            action: () => bridge.post({ type: "pushBranch", name: ref.name }),
          });
        }
        items.push({
          label: t("menu.renameBranch", { name: ref.name }),
          separatorBefore: i === 0 && hasRemote,
          action: () => bridge.post({ type: "renameBranch", name: ref.name }),
        });
        items.push({
          label: t("menu.deleteBranch", { name: ref.name }),
          action: () => bridge.post({ type: "deleteBranch", name: ref.name }),
        });
      });

      showContextMenu(x, y, items);
    },
    onNodeDblClick(commit) {
      bridge.post({ type: "checkout", sha: commit.sha, ref: boxBranchRef(commit) });
    },
    onNodeClick(commit) {
      // Clicking the selected node again deselects it — the panel goes away too.
      if (detailsNodeId === commit.nodeId) {
        hideDetails();
        view.selectPath(null);
        return;
      }
      openDetails(commit);
      // Highlight the path from the root to the selected commit/branch. Stash
      // nodes aren't part of the commit ancestry, so they just clear it.
      view.selectPath(commit.stash ? null : commit.nodeId);
    },
    onCanvasClick() {
      hideDetails();
    },
    onCanvasContextMenu(x, y) {
      // Background menu (right-click off any box): jump to the checkout, reset
      // the view, and the remote ops also offered on the toolbar.
      showContextMenu(x, y, [
        { label: t("menu.jumpHead"), action: goToCheckout },
        {
          label: t("menu.resetView"),
          separatorBefore: true,
          action: () => view.resetView(),
        },
        {
          label: t("toolbar.fetch"),
          separatorBefore: true,
          action: () => {
            renderStatus(() => t("status.fetching"));
            bridge.post({ type: "fetch" });
          },
        },
        {
          label: t("toolbar.pull"),
          action: () => {
            renderStatus(() => t("status.pulling"));
            bridge.post({ type: "pull" });
          },
        },
        {
          label: t("toolbar.sync"),
          action: () => {
            renderStatus(() => t("status.syncing"));
            bridge.post({ type: "sync" });
          },
        },
      ]);
    },
  });

  // Bring the currently checked-out branch/commit into view (toolbar + menu).
  function goToCheckout(): void {
    if (!view.jumpToHead()) renderStatus(() => t("status.noHead"));
  }

  // Effective theme = the user's override (light/dark), or the host's theme when
  // set to "follow host". Applied both to the graph view and to :root so the
  // settings dialog and other body-level UI re-theme together.
  let hostTheme: ThemeTokens = DEFAULT_THEME;
  function effectiveTheme(): ThemeTokens {
    const c = getThemeChoice();
    if (c === "light") return LIGHT_THEME;
    if (c === "dark") return DARK_THEME;
    return hostTheme;
  }
  function applyTheme(): void {
    const tk = effectiveTheme();
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty("--bg", tk.background);
    rootStyle.setProperty("--fg", tk.foreground);
    rootStyle.setProperty("--accent", tk.accent);
    rootStyle.setProperty("--border", tk.border);
    document.documentElement.dataset.theme = tk.kind;
    view.setTheme(tk);
  }

  applyTheme();
  onThemeChange(applyTheme);
  view.setMode(getDisplayMode());

  bridge.onMessage((msg) => {
    switch (msg.type) {
      case "setData":
        renderData(msg.data);
        break;
      case "setTheme":
        hostTheme = msg.theme;
        applyTheme();
        break;
      case "branchCreated": {
        const name = msg.name;
        const sha = msg.sha.slice(0, 7);
        renderStatus(() => t("status.branchCreated", { name, sha }));
        bridge.post({ type: "requestRefresh" });
        break;
      }
      case "opResult": {
        if (msg.result === "error") {
          const message = msg.detail ?? t("status.opFailed");
          renderStatus(() => t("status.error", { message }));
        } else {
          const key = opResultKey(msg.op, msg.result);
          renderStatus(() => t(key));
        }
        // The host refreshes the graph itself after the op; nothing to request.
        break;
      }
      case "commitChanges":
        setChangesFiles(msg.sha, msg.files);
        break;
      case "commitTree":
        setCommitTree(msg.sha, msg.paths);
        break;
      case "treeContentSearchResults":
        setTreeContentSearchResults(msg.sha, msg.query, msg.paths);
        break;
      case "fileDiff":
        setFileDiff(msg.diff);
        break;
      case "workingTreeChanges":
        setWorkingTreeFiles(msg.files);
        break;
      case "workingTreeFileDiff":
        setWorkingTreeFileDiff(msg.diff);
        break;
      case "commitCreated":
        closeCommitDialog();
        renderStatus(() => t("status.commitCreated", { sha: msg.sha.slice(0, 7), message: msg.message }));
        bridge.post({ type: "requestRefresh" });
        break;
      case "fileContent":
        setFileContent(msg.sha, msg.path, msg.text, msg.binary, msg.tooLarge);
        break;
      case "mergePreview":
        setMergePreview(msg.preview);
        break;
      case "mergeFileDiff":
        setMergeFileDiff(msg.diff);
        break;
      case "error": {
        const message = msg.message;
        renderStatus(() => t("status.error", { message }));
        break;
      }
      case "gitPathSelected":
        receiveGitPathFromBrowse(msg.path);
        break;
    }
  });

  // Open the changes dialog for a commit and ask the host for its file list. The
  // dialog then requests each file's diff lazily as the user selects it.
  function startViewChanges(sha: string): void {
    openChangesDialog({
      sha,
      onRequestFile: (file) =>
        bridge.post({
          type: "requestFileDiff",
          sha,
          path: file.path,
          status: file.status,
          oldPath: file.oldPath,
        }),
      onRequestFileContent: (path) =>
        bridge.post({ type: "requestFileContent", sha, path }),
      onSearchTreeContent: (query) =>
        bridge.post({ type: "requestTreeContentSearch", sha, query }),
    });
    bridge.post({ type: "requestCommitChanges", sha });
    bridge.post({ type: "requestCommitTree", sha });
  }

  // Open the merge dialog for "merge `source` into `target`" and ask the host for
  // a dry-run preview (which the dialog fills in when it arrives).
  function startMerge(source: string, target: string): void {
    openMergeDialog({
      source,
      target,
      onRequestFileDiff: (file) =>
        bridge.post({ type: "requestMergeFileDiff", source, path: file.path, status: file.status }),
      onMerge: (message, noFastForward, squash) => {
        renderStatus(() => t("status.merging"));
        bridge.post({ type: "merge", source, message: message || undefined, noFastForward, squash });
      },
    });
    bridge.post({ type: "requestMergePreview", source });
  }

  function renderData(data: GraphData): void {
    closeContextMenu();
    closeNewBranchDialog();
    closeChangesDialog();
    closeCommitDialog();
    closeMergeDialog();
    hideDetails();
    currentHead = data.head ?? null;
    lastData = data;
    view.setData(data, getMainBranch(), showsMergedInTarget());
    updateCommitSearch(false);
    renderStatus(() => {
      const summary = t("status.summary", {
        repo: data.repoName ? `${data.repoName} — ` : "",
        commits: data.commits.length,
        refs: data.refs.length,
      });
      return data.gitCommand ? `${summary} — ${data.gitCommand}` : summary;
    });
  }

  function startCommit(): void {
    openCommitDialog({
      onRequestChanges: () => bridge.post({ type: "requestWorkingTreeChanges" }),
      onRequestDiff: (file) =>
        bridge.post({
          type: "requestWorkingTreeFileDiff",
          path: file.path,
          status: file.status,
          oldPath: file.oldPath,
        }),
      onCommit: (message, files) => {
        renderStatus(() => t("status.committing"));
        bridge.post({ type: "commitWorkingTreeChanges", message, files });
      },
    });
  }

  // The branch a given box represents, so a checkout targets *that* branch
  // rather than resolving by the shared commit (several branches can point at
  // the same commit — resolving by sha would silently pick the wrong one).
  // Prefers a local branch matching the box's lane, then any local branch, then
  // a remote branch; returns undefined for a plain commit so the host falls back
  // to sha-based resolution.
  function boxBranchRef(c: PositionedCommit): string | undefined {
    const local =
      c.refs.find((r) => r.type === "localBranch" && r.name === c.branch) ??
      c.refs.find((r) => r.type === "localBranch");
    if (local) return local.name;
    const remote =
      c.refs.find((r) => r.type === "remoteBranch" && r.name === c.branch) ??
      c.refs.find((r) => r.type === "remoteBranch");
    return remote?.name;
  }

  // Branch names (local + remote) available for the "main branch" picker.
  function branchNames(): string[] {
    if (!lastData) return [];
    return lastData.refs
      .filter((r) => r.type === "localBranch" || r.type === "remoteBranch")
      .map((r) => r.name);
  }

  // Branch names for the New Branch folder tree: local names as-is, remote names
  // with their remote prefix (e.g. "origin/") stripped, de-duplicated.
  function treeBranchNames(): string[] {
    if (!lastData) return [];
    const set = new Set<string>();
    for (const r of lastData.refs) {
      if (r.type === "localBranch") set.add(r.name);
      else if (r.type === "remoteBranch") {
        const slash = r.name.indexOf("/");
        if (slash > 0 && slash < r.name.length - 1) set.add(r.name.slice(slash + 1));
      }
    }
    return [...set];
  }

  // Begin branch creation from a commit: the SVN-style webview dialog when
  // enabled, otherwise defer to the host's own native prompt.
  function startCreateBranch(sha: string, refs: GraphData["refs"]): void {
    if (!getBranchDialogMode()) {
      bridge.post({ type: "createBranch", sha });
      return;
    }
    openNewBranchDialog({
      sha,
      startRefs: refs.filter((r) => r.type !== "head").map((r) => r.name),
      branchNames: treeBranchNames(),
      onCreate: (name, checkout) => bridge.post({ type: "createBranch", sha, name, checkout }),
    });
  }

  // Re-lay-out the existing data when the user changes the main branch.
  onMainBranchChange(() => {
    if (lastData) {
      view.setData(lastData, getMainBranch(), showsMergedInTarget());
      updateCommitSearch(false);
    }
  });

  // Switch the canvas between the modern and classic navigation styles.
  onDisplayModeChange(() => view.setMode(getDisplayMode()));

  // Show (or stop showing) merged-in commits inside the branch that received
  // them. Pure layout — re-lay-out the data already on screen, and reveal the
  // matching legend row so the pale boxes are explained.
  const applyMergedViewToLegend = (): void => {
    if (showsMergedInTarget()) legend.setAttribute("data-merged-in", "");
    else legend.removeAttribute("data-merged-in");
  };
  applyMergedViewToLegend();
  onMergedViewChange(() => {
    applyMergedViewToLegend();
    if (lastData) {
      view.setData(lastData, getMainBranch(), showsMergedInTarget());
      updateCommitSearch(false);
    }
  });

  // Toolbar: refresh + remote ops (fetch/pull/push/sync) + reset view + settings.
  const toolbar = document.createElement("div");
  toolbar.className = "toolbar";
  const refreshBtn = makeButton("refresh", "toolbar.refresh");
  const fetchBtn = makeButton("fetch", "toolbar.fetch");
  const pullBtn = makeButton("pull", "toolbar.pull");
  const pushBtn = makeButton("push", "toolbar.push");
  const commitBtn = makeButton("commit", "toolbar.commit");
  const syncBtn = makeButton("sync", "toolbar.sync");
  const jumpBtn = makeButton("jumpHead", "toolbar.jumpHead");
  const resetBtn = makeButton("reset", "toolbar.reset");
  const settingsBtn = makeButton("settings", "toolbar.settings");
  const searchBox = document.createElement("div");
  searchBox.className = "toolbar-search";
  const searchInput = document.createElement("input");
  searchInput.type = "search";
  searchInput.className = "toolbar-search-input";
  searchInput.placeholder = t("search.placeholder");
  searchInput.setAttribute("aria-label", t("search.tooltip"));
  const searchPrevBtn = document.createElement("button");
  searchPrevBtn.type = "button";
  searchPrevBtn.className = "toolbar-search-nav";
  searchPrevBtn.textContent = "↑";
  searchPrevBtn.title = t("search.previous");
  const searchNextBtn = document.createElement("button");
  searchNextBtn.type = "button";
  searchNextBtn.className = "toolbar-search-nav";
  searchNextBtn.textContent = "↓";
  searchNextBtn.title = t("search.next");
  const searchCount = document.createElement("span");
  searchCount.className = "toolbar-search-count";
  searchBox.append(searchInput, searchPrevBtn, searchNextBtn, searchCount);
  toolbar.append(refreshBtn, commitBtn, fetchBtn, pullBtn, pushBtn, syncBtn, jumpBtn, resetBtn, settingsBtn, searchBox);

  let searchMatches: PositionedCommit[] = [];
  let activeSearchIndex = -1;

  function updateSearchCount(): void {
    if (!searchInput.value.trim()) {
      searchCount.textContent = "";
    } else if (searchMatches.length === 0) {
      searchCount.textContent = t("search.noResults");
    } else {
      searchCount.textContent = t("search.resultCount", {
        index: activeSearchIndex + 1,
        count: searchMatches.length,
      });
    }
    const hasSeveral = searchMatches.length > 1;
    searchPrevBtn.disabled = !hasSeveral;
    searchNextBtn.disabled = !hasSeveral;
  }

  function focusSearchMatch(): void {
    const active = searchMatches[activeSearchIndex];
    const activeNodeId = active?.nodeId ?? null;
    view.setSearchMatches(searchMatches.map((c) => c.nodeId), activeNodeId);
    if (!active) {
      hideDetails();
      updateSearchCount();
      return;
    }
    view.jumpToNode(active.nodeId);
    openDetails(active);
    updateSearchCount();
  }

  function updateCommitSearch(resetIndex = true): void {
    const query = searchInput.value.trim();
    searchMatches = query ? view.findCommitMessages(query) : [];
    if (resetIndex) activeSearchIndex = searchMatches.length > 0 ? 0 : -1;
    else if (searchMatches.length === 0) activeSearchIndex = -1;
    else activeSearchIndex = Math.min(Math.max(activeSearchIndex, 0), searchMatches.length - 1);
    focusSearchMatch();
  }

  function moveSearch(delta: number): void {
    if (searchMatches.length === 0) return;
    activeSearchIndex = (activeSearchIndex + delta + searchMatches.length) % searchMatches.length;
    focusSearchMatch();
  }

  searchInput.addEventListener("input", () => updateCommitSearch(true));
  searchInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      moveSearch(e.shiftKey ? -1 : 1);
    } else if (e.key === "Escape") {
      searchInput.value = "";
      updateCommitSearch(true);
      searchInput.blur();
    }
  });
  searchPrevBtn.addEventListener("click", () => moveSearch(-1));
  searchNextBtn.addEventListener("click", () => moveSearch(1));
  window.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLocaleLowerCase() === "f") {
      e.preventDefault();
      searchInput.focus();
      searchInput.select();
    }
  });

  toolbar.addEventListener("click", (e) => {
    const act = (e.target as HTMLElement).dataset.act;
    if (act === "refresh") bridge.post({ type: "requestRefresh" });
    if (act === "fetch") {
      renderStatus(() => t("status.fetching"));
      bridge.post({ type: "fetch" });
    }
    if (act === "pull") {
      renderStatus(() => t("status.pulling"));
      bridge.post({ type: "pull" });
    }
    if (act === "commit") startCommit();
    if (act === "push") {
      renderStatus(() => t("status.pushing"));
      bridge.post({ type: "push" });
    }
    if (act === "sync") {
      renderStatus(() => t("status.syncing"));
      bridge.post({ type: "sync" });
    }
    if (act === "jumpHead") goToCheckout();
    if (act === "reset") view.resetView();
    if (act === "settings") toggleSettings({ branches: branchNames(), browseGitPath: () => bridge.post({ type: "browseGitPath" }) });
  });
  root.insertBefore(toolbar, mainContent);

  // Re-render all static text when the language changes. The settings and
  // new-branch dialogs re-render themselves in place (they stay open); only the
  // transient context menu is dismissed.
  onLangChange(() => {
    closeContextMenu();
    refreshBtn.textContent = t("toolbar.refresh");
    fetchBtn.textContent = t("toolbar.fetch");
    pullBtn.textContent = t("toolbar.pull");
    pushBtn.textContent = t("toolbar.push");
    commitBtn.textContent = t("toolbar.commit");
    syncBtn.textContent = t("toolbar.sync");
    jumpBtn.textContent = t("toolbar.jumpHead");
    resetBtn.textContent = t("toolbar.reset");
    settingsBtn.textContent = t("toolbar.settings");
    searchInput.placeholder = t("search.placeholder");
    searchInput.setAttribute("aria-label", t("search.tooltip"));
    searchPrevBtn.title = t("search.previous");
    searchNextBtn.title = t("search.next");
    updateSearchCount();
    relabelLegend(legend);
    // Node tooltips are localized too, so redraw the graph in the new language.
    if (lastData) {
      view.setData(lastData, getMainBranch(), showsMergedInTarget());
      updateCommitSearch(false);
    }
    statusText.textContent = statusFn();
    githubLink.textContent = t("footer.github");
  });

  // Notify the host of custom git path whenever it changes in the settings panel.
  onGitSourceChange(() => {
    const mode = getGitMode();
    const path = mode === "custom" ? getCustomGitPath().trim() : null;
    bridge.post({ type: "setGitPath", path: path || null });
  });

  bridge.post({ type: "ready" });
  // If a custom git path is already stored, inform the host immediately after ready.
  if (getGitMode() === "custom") {
    const p = getCustomGitPath().trim();
    if (p) bridge.post({ type: "setGitPath", path: p });
  }
}

/** Map an undo/stash op outcome to its localized status-line key. */
function opResultKey(op: OpKind, result: Exclude<OpResult, "error">): MsgKey {
  if (result === "conflict") {
    if (op === "undo") return "status.undoConflict";
    if (op === "merge") return "status.mergeConflict";
    return "status.stashConflict";
  }
  switch (op) {
    case "undo":
      return "status.commitUndone";
    case "stashApply":
      return "status.stashApplied";
    case "stashPop":
      return "status.stashPopped";
    case "stashDrop":
      return "status.stashDropped";
    case "merge":
      return "status.merged";
  }
}

function makeButton(act: string, key: MsgKey): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.dataset.act = act;
  btn.textContent = t(key);
  return btn;
}

/** A legend row: its own CSS class, the i18n key for its label, and the value
 *  the graph is put into while the row is hovered (see setLegendHighlight). */
type LegendItem = { cls: string; key: MsgKey; hl: string };

/** Box colours — what the *nodes* mean. */
const NODE_LEGEND_ITEMS: LegendItem[] = [
  { cls: "head", key: "legend.head", hl: "head" },
  { cls: "local", key: "legend.local", hl: "local" },
  { cls: "remote", key: "legend.remote", hl: "remote" },
  { cls: "remote-only", key: "legend.remoteOnly", hl: "remote-only" },
  { cls: "tag", key: "legend.tag", hl: "tag" },
  { cls: "commit", key: "legend.commit", hl: "commit" },
  { cls: "stash", key: "legend.stash", hl: "stash" },
  // Only meaningful while merged-in commits are shown in the receiving branch's
  // lane; CSS hides the row unless the legend carries `data-merged-in`.
  { cls: "merged-in", key: "legend.mergedIn", hl: "merged-in" },
];

/** Line styles — what the *connections between* the nodes mean. */
const EDGE_LEGEND_ITEMS: LegendItem[] = [
  { cls: "parent", key: "legend.edgeParent", hl: "parent" },
  { cls: "merge", key: "legend.edgeMerge", hl: "merge" },
  { cls: "branch", key: "legend.edgeBranch", hl: "branch" },
  { cls: "stash-edge", key: "legend.edgeStash", hl: "stash-edge" },
  // Both merged-in line kinds share the merged-in view's visibility gate.
  { cls: "merged-chain", key: "legend.edgeMergedChain", hl: "merged-chain" },
  { cls: "merged-tie", key: "legend.edgeMergedTie", hl: "merged-tie" },
];

/**
 * SVG markup for a line row's swatch. Drawn with the graph's *own* edge classes
 * rather than a copy of their colours, so a swatch can never drift from the line
 * it explains: restyle an edge and its legend entry follows. Kinds whose lines
 * carry a direction get the same arrowhead the graph draws, pointing right.
 */
function edgeSwatch(cls: string): string {
  const line = (edgeCls: string): string =>
    `<path class="edge ${edgeCls}" d="M1 6 H29" />`;
  const head = (arrowCls: string): string =>
    `<path class="edge ${arrowCls} edge-arrow" d="M29 6 L22 2.5 L22 9.5 Z" />`;
  switch (cls) {
    case "merge":
      return line("edge-merge") + head("edge-merge");
    case "branch":
      return line("edge-branch") + head("edge-branch-arrow");
    case "stash-edge":
      return line("edge-stash");
    case "merged-chain":
      return line("edge-merged-chain");
    case "merged-tie":
      return line("edge-merged-tie") + head("edge-merged-tie-arrow");
    default:
      return line("");
  }
}

/**
 * The legend panel: two collapsible sections, boxes and lines. Hovering a row
 * previews that kind in the graph (everything else dims), which is the fastest
 * way to answer "which line is this?" — `onHover` is handed straight to
 * GraphView.setLegendHighlight.
 */
function buildLegend(onHover: (kind: string | null) => void): HTMLElement {
  const legend = document.createElement("div");
  legend.className = "legend";

  const header = document.createElement("div");
  header.className = "legend-header";
  header.innerHTML = `<span class="legend-title"></span><span class="legend-toggle">▲</span>`;
  legend.appendChild(header);

  const body = document.createElement("div");
  body.className = "legend-body";

  const addSection = (titleKey: MsgKey, items: LegendItem[], swatch: (i: LegendItem) => string): void => {
    const section = document.createElement("div");
    section.className = "legend-section";
    const secHeader = document.createElement("div");
    secHeader.className = "legend-section-header";
    secHeader.innerHTML = `<span class="legend-label" data-key="${titleKey}"></span><span class="legend-toggle">▲</span>`;
    const secBody = document.createElement("div");
    secBody.className = "legend-section-body";
    for (const item of items) {
      const row = document.createElement("div");
      row.className = `legend-row ${item.cls}`;
      row.innerHTML = `${swatch(item)}<span class="legend-label" data-key="${item.key}"></span>`;
      row.addEventListener("mouseenter", () => onHover(item.hl));
      secBody.appendChild(row);
    }
    secHeader.addEventListener("click", () => toggleCollapsed(section, secHeader));
    section.append(secHeader, secBody);
    body.appendChild(section);
  };

  addSection("legend.nodes", NODE_LEGEND_ITEMS, (i) => `<span class="legend-swatch ${i.cls}"></span>`);
  addSection(
    "legend.lines",
    EDGE_LEGEND_ITEMS,
    (i) => `<svg class="legend-line" viewBox="0 0 30 12" aria-hidden="true">${edgeSwatch(i.cls)}</svg>`,
  );
  legend.appendChild(body);

  // One clear on the panel is enough: leaving a row always means either entering
  // another row (which sets its own kind) or leaving the panel.
  legend.addEventListener("mouseleave", () => onHover(null));

  header.addEventListener("click", () => toggleCollapsed(legend, header));

  relabelLegend(legend);
  return legend;
}

/** Collapse/expand a legend panel or one of its sections, flipping its caret. */
function toggleCollapsed(el: HTMLElement, header: HTMLElement): void {
  const collapsed = el.hasAttribute("data-collapsed");
  if (collapsed) el.removeAttribute("data-collapsed");
  else el.setAttribute("data-collapsed", "");
  const caret = header.querySelector(".legend-toggle");
  if (caret) caret.textContent = collapsed ? "▲" : "▼";
}

/**
 * Fill (or refresh) the legend's text in the active language. Each label carries
 * its own key, so the two row lists can grow independently without the labels
 * sliding onto the wrong rows.
 */
function relabelLegend(legend: HTMLElement): void {
  const title = legend.querySelector(".legend-title");
  if (title) title.textContent = t("legend.title");
  for (const el of legend.querySelectorAll<HTMLElement>(".legend-label")) {
    const key = el.dataset.key as MsgKey | undefined;
    if (key) el.textContent = t(key);
  }
}

function buildDetailsPanel(): HTMLElement {
  const panel = document.createElement("div");
  panel.className = "details-panel";
  panel.dataset.hidden = "";
  return panel;
}

function showCommitDetails(
  panel: HTMLElement,
  commit: PositionedCommit,
  currentHead: string | null,
  onClose: () => void,
): void {
  delete panel.dataset.hidden;
  panel.innerHTML = "";

  const header = document.createElement("div");
  header.className = "details-header";
  const title = document.createElement("span");
  title.className = "details-title";
  title.textContent = t("details.header");
  header.appendChild(title);
  const closeBtn = document.createElement("button");
  closeBtn.className = "settings-close-x";
  closeBtn.type = "button";
  closeBtn.textContent = "×";
  closeBtn.setAttribute("aria-label", t("details.close"));
  closeBtn.addEventListener("click", onClose);
  header.appendChild(closeBtn);
  panel.appendChild(header);

  addDetailRow(panel, t("details.sha"), commit.sha, "details-sha");
  addDetailRow(panel, t("details.shortSha"), commit.sha.slice(0, 7), "details-shortsha");
  addDetailRow(panel, t("details.message"), commit.summary, "details-message");
  addDetailRow(panel, t("details.author"), `${commit.author} <${commit.authorEmail}>`);
  addDetailRow(panel, t("details.date"), formatDetailDate(commit.date));

  // Flag the commit the working tree is currently checked out on.
  if (currentHead && commit.sha === currentHead) {
    const section = document.createElement("div");
    section.className = "details-section";
    const lbl = document.createElement("div");
    lbl.className = "details-label";
    lbl.textContent = t("details.location");
    section.appendChild(lbl);
    const badge = document.createElement("span");
    badge.className = "details-current";
    badge.textContent = t("details.currentHead");
    section.appendChild(badge);
    panel.appendChild(section);
  }

  if (commit.refs.length > 0) {
    const section = document.createElement("div");
    section.className = "details-section";
    const lbl = document.createElement("div");
    lbl.className = "details-label";
    lbl.textContent = t("details.labels");
    section.appendChild(lbl);
    const chips = document.createElement("div");
    chips.className = "details-chips";
    for (const ref of commit.refs) {
      const chip = document.createElement("span");
      chip.className = `details-chip details-chip-${ref.type}`;
      if (ref.isCurrent) chip.classList.add("details-chip-current");
      chip.textContent = ref.type === "head" ? "HEAD" : ref.name;
      chips.appendChild(chip);
    }
    section.appendChild(chips);
    panel.appendChild(section);
  }
}

function addDetailRow(panel: HTMLElement, label: string, value: string, extraClass?: string): void {
  const section = document.createElement("div");
  section.className = "details-section";
  const lbl = document.createElement("div");
  lbl.className = "details-label";
  lbl.textContent = label;
  const val = document.createElement("div");
  val.className = extraClass ? `details-value ${extraClass}` : "details-value";
  val.textContent = value;
  section.appendChild(lbl);
  section.appendChild(val);
  panel.appendChild(section);
}

function formatDetailDate(isoDate: string): string {
  if (!isoDate) return "";
  return isoDate.slice(0, 19).replace("T", " ");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
